import { Card, CardActions, CardContent, CardHeader, Modal, Stack, TextField } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useAuth } from '../../../authentication/useAuth'
import GreyButton from '../../../components/GreyButton'
import { setAllSettings } from '../../../store/global'
import instance from '../../../utils/imsAxios'
import useSnackbar from '../../../hooks/useSnackbar'
import BusyButton from '../../../components/BusyButton'
import useBusy from '../../../hooks/useBusy'

const EditSettingsModal = ({ open, setOpen }) => {
    const [busy, run] = useBusy()
    const { companyId, user, canEdit } = useAuth();
    const dispatch = useDispatch();
    const { settings } = useSelector(state => state.global);
    const [numWeeks, setNumWeeks] = useState("");
    const [roadConditionRates, setRoadConditionRates] = useState({});
    const [ccipPricing, setCcipPricing] = useState({
        ccipMinGramsPerTon: "",
        ccipGoldPricePerGram: "",
        ccipMinimumFee: "",
        ccipMaximumFee: ""
    });
    const { openSnackbar } = useSnackbar();
    const canEditMinimumCharge = canEdit('job-card-settings-minimum-charge');
    const canEditGoldPrice = canEdit('job-card-settings-gold-price');
    const canEditRoadRates = canEdit('job-card-settings-road-rates');

    const handleSave = async (event) => {
        try {
            event.preventDefault();
            const updatedBy = `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim();
            const gramsPerTon = parseFloat(ccipPricing.ccipMinGramsPerTon);
            const goldPricePerGram = parseFloat(ccipPricing.ccipGoldPricePerGram);

            if (canEditMinimumCharge && (!Number.isFinite(gramsPerTon) || gramsPerTon <= 0)) {
                openSnackbar('error', 'Please enter a valid CCIP minimum grams / ton value');
                return;
            }

            if (canEditGoldPrice && (!Number.isFinite(goldPricePerGram) || goldPricePerGram <= 0)) {
                openSnackbar('error', 'Please enter a valid CCIP gold price / gram value');
                return;
            }

            // Blank fee = no limit
            const fee = (value) => (value === "" || value === null || value === undefined ? null : parseFloat(value));
            const minimumFee = fee(ccipPricing.ccipMinimumFee);
            const maximumFee = fee(ccipPricing.ccipMaximumFee);
            if (canEditMinimumCharge && [minimumFee, maximumFee].some(v => v !== null && (!Number.isFinite(v) || v < 0))) {
                openSnackbar('error', 'Please enter a valid CCIP minimum and maximum fee, or leave them blank');
                return;
            }
            if (canEditMinimumCharge && minimumFee !== null && maximumFee !== null && maximumFee < minimumFee) {
                openSnackbar('error', 'The CCIP maximum fee cannot be less than the minimum fee');
                return;
            }

            const nextCcipPricing = {
                ...settings?.ccipPricing
            };
            if (canEditMinimumCharge) Object.assign(nextCcipPricing, { ccipMinGramsPerTon: gramsPerTon, ccipMinimumFee: minimumFee, ccipMaximumFee: maximumFee });
            if (canEditGoldPrice) nextCcipPricing.ccipGoldPricePerGram = goldPricePerGram;
            if (canEditMinimumCharge || canEditGoldPrice) {
                nextCcipPricing.updatedAt = new Date().toISOString();
                nextCcipPricing.updatedBy = updatedBy || null;
            }

            const newSettings = {
                ...settings,
                numWeeks,
                roadConditionRates,
                ccipPricing: nextCcipPricing
            };
            const { data } = await instance.post('/company/settings', { settings: { ...newSettings }, companyId });
            dispatch(setAllSettings(data))
            setOpen(false);
            setNumWeeks("");
            openSnackbar('success', 'Settings saved successfully')
        }
        catch (error) {
            openSnackbar('error', 'Error saving settings')
        }
    }

    useEffect(() => {
        setNumWeeks(settings?.numWeeks ?? "");
        setRoadConditionRates(settings?.roadConditionRates ?? {});
        setCcipPricing({
            ccipMinGramsPerTon: settings?.ccipPricing?.ccipMinGramsPerTon ?? "",
            ccipGoldPricePerGram: settings?.ccipPricing?.ccipGoldPricePerGram ?? "",
            ccipMinimumFee: settings?.ccipPricing?.ccipMinimumFee ?? "",
            ccipMaximumFee: settings?.ccipPricing?.ccipMaximumFee ?? ""
        });
    }, [dispatch, companyId, settings, open])


    return (
        <Modal
            sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
            }}
            open={open}
        >
            <Card>
                <form onSubmit={run(handleSave)}>
                    <CardHeader title="Job Card Settings" />
                    <CardContent>
                        <Stack spacing={2}>
                            <TextField size="small" label="Validity Period" value={numWeeks ?? ""} onChange={(event) => setNumWeeks(event.target.value)} InputProps={{ endAdornment: "Weeks" }} />
                            <TextField size="small" type='number' label="Good Road Rate" value={roadConditionRates?.goodRoadRate ?? ""} disabled={!canEditRoadRates} onChange={(event) => setRoadConditionRates(prev => {
                                return { ...prev, goodRoadRate: event.target.value }
                            })} InputProps={{ startAdornment: "$", style: { gap: '8px' } }} />
                            <TextField size="small" label="Bad Road Rate" value={roadConditionRates?.badRoadRate ?? ""} disabled={!canEditRoadRates} onChange={(event) => setRoadConditionRates(prev => {
                                return { ...prev, badRoadRate: event.target.value }
                            })} InputProps={{ startAdornment: `$ `, style: { gap: '8px' } }} />
                            <TextField
                                size="small"
                                type='number'
                                disabled={!canEditMinimumCharge}
                                label="CCIP Minimum Grams / Ton"
                                value={ccipPricing?.ccipMinGramsPerTon ?? ""}
                                onChange={(event) => setCcipPricing(prev => ({ ...prev, ccipMinGramsPerTon: event.target.value }))}
                            />
                            <TextField
                                size="small"
                                type='number'
                                disabled={!canEditGoldPrice}
                                label="CCIP Gold Price / Gram"
                                value={ccipPricing?.ccipGoldPricePerGram ?? ""}
                                onChange={(event) => setCcipPricing(prev => ({ ...prev, ccipGoldPricePerGram: event.target.value }))}
                                InputProps={{ startAdornment: "$", style: { gap: '8px' } }}
                            />
                            <TextField
                                size="small"
                                type='number'
                                disabled={!canEditMinimumCharge}
                                label="CCIP Minimum Fee"
                                helperText="Leave blank for no minimum"
                                value={ccipPricing?.ccipMinimumFee ?? ""}
                                onChange={(event) => setCcipPricing(prev => ({ ...prev, ccipMinimumFee: event.target.value }))}
                                InputProps={{ startAdornment: "$", style: { gap: '8px' } }}
                            />
                            <TextField
                                size="small"
                                type='number'
                                disabled={!canEditMinimumCharge}
                                label="CCIP Maximum Fee"
                                helperText="Leave blank for no maximum"
                                value={ccipPricing?.ccipMaximumFee ?? ""}
                                onChange={(event) => setCcipPricing(prev => ({ ...prev, ccipMaximumFee: event.target.value }))}
                                InputProps={{ startAdornment: "$", style: { gap: '8px' } }}
                            />
                        </Stack>
                    </CardContent>
                    <CardActions>
                        <Stack width="100%" direction="row" justifyContent="space-between">
                            <BusyButton busy={busy} variant="contained" size="small" type='submit'>Save & Close</BusyButton>
                            <GreyButton size="small" onClick={() => { setOpen(false); setNumWeeks("") }}>Cancel</GreyButton>
                        </Stack>
                    </CardActions>
                </form>
            </Card>
        </Modal>
    )
}

export default EditSettingsModal
